import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, Loader2, ListChecks, ChevronLeft, ChevronRight, Car } from 'lucide-react';
import { resolveBuyerTheme } from '../components/showroom/buyerTheme';
import { attachTactile, setOrgTactile } from '../components/showroom/tactile';
import { VehicleSpecsPanel } from '../components/showroom/VehicleSpecsPanel';
import useBuyerHeartbeat from '../components/showroom/useBuyerHeartbeat';
import { buyerSessionId } from '../components/showroom/useFormDraft';
import { vehiclePublicAPI } from '../lib/api';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const TABS = ['specs', 'details', 'images'];

/** Vehicle details & specs — its own screen, same pattern as F&I and Trade-In
 *  (a route off the vehicle page, never a modal). Three tabs; the photo
 *  carousel lives HERE now, not on the hero. */
export default function VehicleDetailsSpecs() {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const rootRef = useRef(null);
    const touchX = useRef(null);
    const { t } = useTranslation();
    // Still on the buyer journey, just not on the showroom — keep the sales
    // desk's "live now" honest while they read the specs.
    useBuyerHeartbeat(cardId);
    useEffect(() => attachTactile(document.body), []);
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [idx, setIdx] = useState(0);
    const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'specs';
    const setTab = (v) => setParams({ tab: v }, { replace: true });

    useEffect(() => {
        axios.get(`${API}/api/vehicle/${cardId}`)
            .then((r) => { setOrgTactile(r.data?.action_noise); setData(r.data); })
            .catch(() => setErr('load'));
        // Reading the specs is part of the visit — the desk sees it live.
        vehiclePublicAPI.activity(cardId, 'details_viewed', buyerSessionId(cardId)).catch(() => {});
    }, [cardId]);

    const th = resolveBuyerTheme(data?.buyer_theme);

    if (err) {
        return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-300" data-testid="vehicle-details-error">
            {t('vehicleExp.unavailable', 'This page is unavailable')}
        </div>;
    }
    if (!data) {
        return <div className="min-h-screen flex items-center justify-center bg-neutral-950" data-testid="vehicle-details-loading">
            <Loader2 className="w-7 h-7 animate-spin text-neutral-500" />
        </div>;
    }

    const v = data.vehicle || {};
    const title = [v.year, v.make, v.model].filter(Boolean).join(' ');
    // The hero (index 0) stays on the vehicle page; this tab is everything else.
    const gallery = (v.photos || []).slice(1);
    const go = (d) => { if (gallery.length) setIdx((i) => (i + d + gallery.length) % gallery.length); };
    const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touchX.current = null;
    };

    const TabBtn = ({ id, label }) => (
        <button type="button" onClick={() => setTab(id)} data-testid={`vd-tab-${id}`} data-tactile="select"
            aria-selected={tab === id}
            className={`flex-1 min-w-0 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors ${tab === id
                ? 'bg-emerald-500 text-neutral-950'
                : `${th.panelSolid} ${th.muted}`}`}>
            {label}
        </button>
    );

    return (
        <div ref={rootRef} className={`min-h-screen ${th.page}`} data-testid="vehicle-details-page">
            <div className="max-w-md mx-auto pt-5 pb-16">
                <div className="px-4">
                    <button onClick={() => navigate(`/vehicle/${cardId}`)} data-testid="vehicle-details-back"
                        className={`flex items-center gap-1 text-sm ${th.muted} hover:opacity-80`}>
                        <ArrowLeft className="w-4 h-4" />{t('common.back', 'Back')}
                    </button>

                    <header className="mt-3">
                        <h1 className={`text-2xl font-extrabold flex items-center gap-2 ${th.title}`} data-testid="vehicle-details-title">
                            <ListChecks className="w-6 h-6" style={{ color: '#38BDF8' }} />
                            {t('vehicleExp.detailsSpecsTitle', 'Vehicle details & specs')}
                        </h1>
                        <p className={`text-sm mt-1 ${th.muted}`} data-testid="vehicle-details-context">{title}</p>
                    </header>

                    <div className="flex gap-2 mt-4" data-testid="vd-tabs">
                        <TabBtn id="specs" label={t('vehicleExp.tabSpecs', 'Specs')} />
                        <TabBtn id="details" label={t('vehicleExp.tabDetails', 'Details')} />
                        <TabBtn id="images" label={t('vehicleExp.tabImages', 'Images')} />
                    </div>
                </div>

                {tab === 'images' ? (
                    <div className="px-4 mt-4" data-testid="vd-images">
                        {gallery.length === 0 ? (
                            <div className={`rounded-2xl p-8 flex flex-col items-center gap-2 ${th.panelSolid}`} data-testid="vd-images-empty">
                                <Car className="w-10 h-10 opacity-40" />
                                <p className={`text-sm ${th.muted}`}>{t('vehicleExp.noMorePhotos', 'No other photos for this vehicle.')}</p>
                            </div>
                        ) : (
                            <>
                                <div className="relative rounded-2xl overflow-hidden bg-neutral-900"
                                    onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                                    <img src={gallery[idx]} alt={title} className="w-full aspect-[4/3] object-cover" data-testid="vd-image" />
                                    {gallery.length > 1 && (
                                        <>
                                            <button onClick={() => go(-1)} aria-label={t('vehicleExp.prevPhoto', 'Previous photo')}
                                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/45 hover:bg-black/70 rounded-full p-2 transition-colors" data-testid="vd-image-prev">
                                                <ChevronLeft className="w-5 h-5" />
                                            </button>
                                            <button onClick={() => go(1)} aria-label={t('vehicleExp.nextPhoto', 'Next photo')}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/45 hover:bg-black/70 rounded-full p-2 transition-colors" data-testid="vd-image-next">
                                                <ChevronRight className="w-5 h-5" />
                                            </button>
                                            <div className="absolute top-3 right-3 text-[11px] font-medium bg-black/50 rounded-full px-2.5 py-1" data-testid="vd-image-counter">
                                                {idx + 1} / {gallery.length}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {gallery.length > 1 && (
                                    <div className="flex justify-center gap-1.5 mt-3" data-testid="vd-image-dots">
                                        {gallery.map((_, i) => (
                                            <button key={i} onClick={() => setIdx(i)} aria-label={`${i + 1}`}
                                                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-emerald-400' : 'w-1.5 bg-white/40'}`} />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <VehicleSpecsPanel vehicle={v} th={th} t={t} defaultOpen show={tab} />
                )}
            </div>
        </div>
    );
}
